import React from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import { useSessionBootstrap } from "../features/auth/hooks/useSessionBootstrap";
import { ConsentRequired } from "../features/auth/components/ConsentRequired";
import {
  WorkspaceLoading,
  WorkspaceLoadFailed,
} from "../features/auth/components/WorkspaceLoading";
import { useTrialAccess } from "../features/billing/hooks/useTrialAccess";
import { ROUTES } from "./paths";

/**
 * Shown while Clerk restores the session from its cookie. Both guards must
 * wait for it: redirecting on `isSignedIn === false` before `isLoaded` would
 * bounce every signed-in user to the login screen on a hard refresh.
 */
const SessionLoading: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-white">
    <span
      role="status"
      aria-label="Loading"
      className="h-8 w-8 rounded-full border-2 border-[#DCE0E4] border-t-[#00C2CB] animate-spin"
    />
  </div>
);

/** Wraps the signed-in screens. Remembers where the user was headed so the
 *  login screen can send them back there once they are in. */
export const RequireAuth: React.FC = () => {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const location = useLocation();

  // Every way into the signed-in app passes through here - the OTP screen, the
  // Google handshake redirect, a refresh, a deep link - which makes this the
  // one place the backend handshake can be run without depending on which
  // screen the session was created on. It no-ops until signed in, and again
  // for any session it has already synced.
  const bootstrap = useSessionBootstrap();

  if (!isLoaded) return <SessionLoading />;
  if (!isSignedIn) {
    return <Navigate to={ROUTES.login} replace state={{ from: location }} />;
  }

  // Nothing behind this guard renders until whoami and the workspace call have
  // both answered. Holding here rather than letting the screens mount and fetch
  // is what keeps a signed-in user from seeing an empty dashboard first: every
  // page below assumes the workspace exists, which is only true once this is
  // done.
  if (bootstrap.phase === "failed") {
    return (
      <WorkspaceLoadFailed
        message={bootstrap.message}
        onRetry={bootstrap.retry}
      />
    );
  }

  const atConsentGate = location.pathname === ROUTES.consentRequired;

  // The one point in the handshake that waits on the user rather than the
  // network: the backend still wants their agreement to something, and this
  // session has no answer to give on their behalf. Nothing further is created
  // for them until it does.
  if (bootstrap.phase === "consent") {
    // A screen of its own gets a URL of its own. Left on whatever path the user
    // was heading for, the address bar would read /dashboard under a consent
    // form - which is a lie a reload would then act on. Where they were going
    // rides along in state, to be returned to below.
    if (!atConsentGate) {
      return (
        <Navigate
          to={ROUTES.consentRequired}
          replace
          state={{ from: `${location.pathname}${location.search}` }}
        />
      );
    }

    return (
      <ConsentRequired
        documents={bootstrap.documents}
        getToken={getToken}
        onRecorded={bootstrap.consentRecorded}
      />
    );
  }

  if (bootstrap.phase !== "ready") {
    return <WorkspaceLoading step={bootstrap.step} />;
  }

  // Nothing is owed any more and the gate's path is not a screen, so whoever is
  // standing on it is sent on to wherever the gate interrupted them. This also
  // catches a stale link to it arriving with nothing outstanding.
  if (atConsentGate) {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from ?? ROUTES.dashboard} replace />;
  }

  return <Outlet />;
};

/**
 * The ask itself: `can_start_trial`, and nothing rendered until it answers.
 *
 * Its own component so the call is only ever made where the answer is used —
 * mounting it is what starts the request, and `OfferTrialFirst` below mounts it
 * on one path only.
 */
const TrialCheck: React.FC = () => {
  const { access, status } = useTrialAccess();

  if (status === "loading") return <SessionLoading />;
  if (status === "ready" && access?.canStartTrial) {
    return <Navigate to={ROUTES.trial} replace />;
  }

  return <Outlet />;
};

/**
 * Offers the free trial before the app, to a workspace that can still take one.
 *
 * `GET /billing/subscription/` answers `can_start_trial`, and that is the whole
 * of the decision — already paying, already trialling and has had one all come
 * back as `false`, and all three mean the same thing here: show them the app.
 *
 * ## Above the shell, and asking on one path
 *
 * It sits *outside* `AppLayout` rather than inside it. Within the layout the
 * check is made with the sidebar and the header already on screen, so a reader
 * being sent to the offer watches the app draw itself and then vanish — which
 * is the flash this is here to prevent. Out here, nothing of the app is painted
 * until the answer is in.
 *
 * That puts it in front of every signed-in screen, so it asks on one path: the
 * dashboard, where somebody arrives after signing in and where the rail's first
 * item goes. Everywhere else passes straight through, and `TrialCheck` is not
 * mounted at all, so no request is made — a link to one client's page has no
 * business dragging its reader off to a price card, and no business waiting on
 * a billing call either.
 *
 * The other half of this pair lives on the offer itself: `/create-account`
 * sends anyone who *cannot* start one straight back here. The two cannot loop —
 * they read one boolean and answer opposite sides of it — and a read that fails
 * resolves towards the app here and towards the offer there, so neither failure
 * traps anybody.
 */
export const OfferTrialFirst: React.FC = () => {
  const { pathname } = useLocation();

  return pathname === ROUTES.dashboard ? <TrialCheck /> : <Outlet />;
};

/**
 * Wraps the auth screens, so a signed-in user landing on /login goes to the app
 * instead of being asked to log in again.
 *
 * To the trial rather than the dashboard, the same as every other way in: that
 * screen asks whether there is a trial to be had and forwards to the dashboard
 * when there is not, so nobody is sent to a URL they are about to be moved off.
 */
export const RedirectIfSignedIn: React.FC = () => {
  const { isLoaded, isSignedIn } = useAuth();

  if (!isLoaded) return <SessionLoading />;
  if (isSignedIn) return <Navigate to={ROUTES.trial} replace />;
  return <Outlet />;
};
